import { Icon } from '@eventbrite/eds-icon';
import { CalendarFill } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import {
    EventDetail,
    EventDetailContent,
    EventDetailHeading,
    EventDetailIcon,
    ICON_COLOR,
    ICON_SIZE,
} from '../EventDetail/EventDetail';

export type DateAndTimeProps = {
    childEvents?: Array<{
        url: string;
        formatted_start_date: string;
    }>;
    displayDate: string;
    displayEndDateWithTz: string;
    displayStartDateWithTz: string;
    doorTime?: string;
    doorTimeLineOne?: string;
    doorTimeLineTwo?: string;
    doorTimeLineThree?: string;
    dateLineTwo: string;
    dateLineOne: string;
    hideEndDate?: boolean;
    isSameStartEndDate: boolean;
    isSeriesParent?: boolean;
    shouldDisplayCrawlableSeriesChildrenLinks: boolean;
    shouldShowDoorTime?: string;
};

export const DateAndTime = ({
    childEvents,
    displayDate,
    displayEndDateWithTz,
    displayStartDateWithTz,
    dateLineTwo,
    dateLineOne,
    hideEndDate,
    isSameStartEndDate,
    isSeriesParent,
    shouldDisplayCrawlableSeriesChildrenLinks,
    shouldShowDoorTime,
    doorTime,
    doorTimeLineOne,
    doorTimeLineTwo,
    doorTimeLineThree,
}: DateAndTimeProps) => {
    if (
        (isSeriesParent && !shouldDisplayCrawlableSeriesChildrenLinks) ||
        !displayDate
    ) {
        return null;
    }

    const SeriesComponent = () => {
        if (shouldDisplayCrawlableSeriesChildrenLinks && isSeriesParent) {
            return (
                <div className="event-detail__series-events">
                    <ul>
                        {childEvents?.map((child, index) => (
                            <li key={index}>
                                <a href={child.url}>
                                    {child.formatted_start_date}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            );
        } else if (childEvents && childEvents.length > 0 && !isSeriesParent) {
            return (
                <div
                    id="listings-root__series-events-desktop"
                    className="event-detail__series-events"
                ></div>
            );
        }
        return null;
    };

    const LABEL_ID = 'date-time-heading';

    const DateTimeLines = () => {
        return !isSeriesParent ? (
            <>
                <EventDetailHeading>
                    <h3 id={LABEL_ID}>{gettext('Date and time')}</h3>
                </EventDetailHeading>
                <div>
                    <meta
                        content={displayStartDateWithTz}
                        data-testid="event-start-date-tz"
                    />
                    <meta
                        content={displayEndDateWithTz}
                        data-testid="event-end-date-tz"
                    />
                    {shouldShowDoorTime ? (
                        <div data-testid="event-door-time-block">
                            <time>
                                <p data-testid="event-start-date-time">
                                    {doorTimeLineOne}
                                </p>
                                {!isSameStartEndDate && !hideEndDate && (
                                    <p data-testid="event-end-date-time">
                                        {doorTimeLineTwo}
                                    </p>
                                )}
                                {doorTime && (
                                    <p data-testid="event-door-time-line">
                                        {doorTimeLineThree}
                                    </p>
                                )}
                            </time>
                        </div>
                    ) : (
                        <time>
                            <p data-testid="event-date">{dateLineOne}</p>
                            <p data-testid="event-time">{dateLineTwo}</p>
                        </time>
                    )}
                </div>
            </>
        ) : null;
    };

    return (
        <EventDetail ariaLabelledBy={LABEL_ID}>
            <EventDetailIcon>
                <Icon
                    type={<CalendarFill />}
                    size={ICON_SIZE}
                    color={ICON_COLOR}
                />
            </EventDetailIcon>
            <EventDetailContent>
                <DateTimeLines />
                <SeriesComponent />
            </EventDetailContent>
        </EventDetail>
    );
};
